import type { Enduser } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { Label } from "@tellescope/ui";
import { cn } from "@tellescope/utils";
import { FieldDescription } from "../../FieldDescription";
import type { TellescopeFormFieldBaseProps, TellescopeFieldResponse } from "../../types";
import { BridgeEligibilityFieldContent } from "./BridgeEligibilityFieldContent";
import { BridgeEligibilityStaffPanel } from "./BridgeEligibilityStaffPanel";
import { BridgeEligibilityPhase, useBridgeEligibilityCheck } from "./useBridgeEligibilityCheck";

export interface BridgeEligibilityFieldProps extends TellescopeFormFieldBaseProps<"Bridge Eligibility"> {
  enduserId: string;
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
  priorResponses: TellescopeFieldResponse[];
  sessionType?: "enduser" | "user";
}

export const BridgeEligibilityField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  options,
  enduserId,
  enduser,
  priorResponses,
  className,
  error,
  sessionType = "enduser",
}: BridgeEligibilityFieldProps) => {
  const serviceTypeIds = options?.bridgeServiceTypeIds ?? [];
  const eligibilityType = options?.bridgeEligibilityType ?? "Soft";

  const eligibilityCheck = useBridgeEligibilityCheck({
    value,
    onChange,
    enduserId,
    enduser,
    priorResponses,
    serviceTypeIds,
    bridgeEligibilityType: options?.bridgeEligibilityType,
    sessionType,
  });
  const { status } = value || eligibilityCheck.aggregateResult;
  const phase: BridgeEligibilityPhase = value ? "cached" : eligibilityCheck.phase;

  return (
    <div className={cn("space-y-1", className)}>
      {!!title && <Label required={false}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {sessionType === "user" ? (
        <BridgeEligibilityStaffPanel
          eligibilityType={eligibilityType}
          serviceTypeIds={serviceTypeIds}
          state={eligibilityCheck.state}
          payerId={eligibilityCheck.payerId}
          memberId={eligibilityCheck.memberId}
          isChecking={eligibilityCheck.isChecking}
          isPolling={eligibilityCheck.phase === "polling-hard"}
          errorMessage={eligibilityCheck.errorMessage}
          value={value}
          onCheckProvider={eligibilityCheck.triggerSoftCheck}
          onCheckService={eligibilityCheck.triggerHardCheck}
        />
      ) : (
        <BridgeEligibilityFieldContent phase={phase} status={status} />
      )}
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
